import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";

const Index = () => {
  return (
    <main>
      {/* Hero */}
      <section className="relative flex min-h-[70vh] items-center justify-center overflow-hidden">
        <img
          src="/images/hero-home.jpg"
          alt="Orizzonte marino sereno, immagine simbolo del Centro Arcipelago, equipe clinica multidisciplinare a Milano"
          width={1809}
          height={892}
          sizes="100vw"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-foreground/40" />
        <div className="relative z-10 px-4 py-20 text-center">
          <img
            src="/images/logo.jpeg"
            alt="Logo Centro Arcipelago"
            width={300}
            height={300}
            className="mx-auto mb-6 h-20 w-20 rounded-full bg-background/80 object-contain p-2 shadow-lg md:h-24 md:w-24"
          />
          <h1 className="mb-4 font-serif text-4xl font-bold tracking-tight text-white drop-shadow-lg md:text-6xl lg:text-7xl animate-fade-in-up">
            Centro<br />Arcipelago
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg font-light text-white/90 drop-shadow md:text-xl" style={{ animationDelay: "0.2s" }}>
            Equipe clinica multidisciplinare per la cura della corporeità sintomatica.
          </p>
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Link to="/contatti">
              <Button size="lg" className="bg-primary text-primary-foreground shadow-md hover:bg-accent hover:shadow-lg transition-shadow">
                Contattaci
              </Button>
            </Link>
            <Link to="/chi-siamo">
              <Button size="lg" variant="outline" className="border-2 border-white bg-white/15 text-white backdrop-blur-sm hover:bg-white/30 hover:text-white shadow-md">
                Scopri l'equipe
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* La nostra visione */}
      <section className="section-padding bg-background">
        <div className="container-narrow">
          <AnimatedSection>
            <h2 className="mb-6 text-3xl font-bold text-foreground md:text-4xl">La nostra visione</h2>
          </AnimatedSection>
          <AnimatedSection delay={100}>
            <p className="mb-6 text-base leading-relaxed text-muted-foreground md:text-lg">
              Il Centro Arcipelago è un'equipe multidisciplinare, che si occupa della corporeità sintomatica del paziente in una logica di lavoro integrata, clinica, di ricerca-formazione e preventiva, secondo il paradigma epistemico della complessità, ossia guardando all'umano come unitario e ricorsivo, per il quale:
            </p>
          </AnimatedSection>
          <AnimatedSection delay={200}>
            <blockquote className="mb-8 border-l-4 border-primary py-3 pl-6 font-serif text-lg italic text-foreground/80 md:text-xl">
              "La singola parte è il tutto e il tutto è la singola parte. Le varie componenti o le diverse funzioni devono essere colte nella loro interazione."
            </blockquote>
          </AnimatedSection>

          <div className="grid gap-8 md:grid-cols-2 md:items-center">
            <AnimatedSection delay={300}>
              <div className="space-y-4">
                <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
                  All'interno di questa prospettiva 'a frattale', gli specialisti del Centro Arcipelago considerano il sintomo, che abbia manifestazioni corporee, come espressione metaforica e sincretica di tutto il paziente, secondo la logica ricorsiva del <strong className="text-foreground">'io sono il mio sintomo'</strong>.
                </p>
                <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
                  Intendiamo, inoltre, la sintomatologia corporea sempre in ottica relazionale, in quanto consideriamo la mente come intersoggettiva, perché l'identità e la coscienza di sé si formano nel contesto delle relazioni, non in un isolamento intrapsichico. In tal senso, il sintomo è anche espressione dei legami del paziente.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={400}>
              <div className="overflow-hidden rounded-xl shadow-md">
                <img
                  src="/images/home-vertical.jpg"
                  alt="Paesaggio naturale evocativo"
                  width={1934}
                  height={1249}
                  sizes="(min-width: 768px) 50vw, 100vw"
                  className="h-72 w-full object-cover transition-transform duration-500 hover:scale-105 md:h-80"
                  loading="lazy"
                />
              </div>
            </AnimatedSection>
          </div>

          <AnimatedSection delay={200}>
            <p className="mt-8 text-base leading-relaxed text-muted-foreground md:text-lg">
              Questo impianto teorico è il razionale dei nostri interventi clinici, che consistono del confronto continuo di diverse professionalità (medico-psichiatra e psicoterapeuta, medico-internista, biologo nutrizionista, psicologo-psicoterapeuta), che, nella complessità del lavoro di rete, rispecchia la nostra visione complessa dell'umano e della cura ed è la nostra proposta ai pazienti.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Interventi proposti */}
      <section className="section-padding bg-secondary/50">
        <div className="container-narrow">
          <AnimatedSection>
            <h2 className="mb-4 text-3xl font-bold text-foreground md:text-4xl">Interventi proposti</h2>
            <p className="mb-8 text-base leading-relaxed text-muted-foreground md:text-lg">
              Il Centro Arcipelago propone interventi per il trattamento delle seguenti situazioni cliniche con manifestazioni corporee:
            </p>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <div className="mb-8 flex flex-wrap gap-3">
              {[
                "Autolesionismo",
                "Agiti impulsivi",
                "Disturbi alimentari (DNA)",
                "Somatizzazioni",
                "Ansia e attacchi di panico",
                "Tematiche legate alle transizioni",
                "Tematiche legate alla fertilità",
              ].map((area) => (
                <span
                  key={area}
                  className="rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-primary/20"
                >
                  {area}
                </span>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <p className="mb-4 text-base leading-relaxed text-muted-foreground md:text-lg">
              All'interno di differenti dispositivi:
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {["Individuali", "Di coppia", "Familiari", "Gruppali", "Psico-educativi", "Con uno o più specialisti"].map((d) => (
                <div key={d} className="flex items-center gap-2 rounded-lg bg-card p-4 text-sm font-medium text-foreground shadow-sm transition-shadow hover:shadow-md">
                  <div className="h-2 w-2 shrink-0 rounded-full bg-primary" />
                  {d}
                </div>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection delay={300}>
            <p className="mt-6 text-sm text-muted-foreground">
              Nelle diverse fasce d'età, in particolare <strong className="text-foreground">adolescenza ed età adulta</strong>.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA finale */}
      <section className="section-padding bg-primary/10">
        <div className="container-narrow text-center">
          <AnimatedSection>
            <h2 className="mb-4 text-3xl font-bold text-foreground md:text-4xl">Vuoi saperne di più?</h2>
            <p className="mb-6 text-muted-foreground">Scrivici per informazioni.</p>
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link to="/contatti">
                <Button size="lg" className="bg-primary text-primary-foreground shadow-md hover:bg-accent hover:shadow-lg transition-shadow">
                  Contattaci <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="mailto:info@centroarcipelago.it">
                <Button size="lg" variant="outline" className="border-border text-foreground hover:bg-secondary">
                  info@centroarcipelago.it
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
};

export default Index;
